import { useCallback } from "react";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";
import { copyTextToClipboard } from "utils/common";

import Box from "components/Box";
import Button from "components/Button";
import Dialog, { DialogActions } from "components/Dialog";
import { Link } from "components/Link";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import {
  createShareableMemoLink,
  expireShareableMemoLink,
  useShareableMemoLink,
} from "entities/Memo/sdk";

export interface IShareableLinkPopup {
  dealId: number;
  memoId: number;
  open: boolean;
  onClose: () => void;
}

const ShareableLinkPopup = ({
  dealId,
  memoId,
  open,
  onClose,
}: IShareableLinkPopup) => {
  const { show: showConfirmationDialog } = useConfirmationDialog();
  const { data: shareableMemoLink, mutate: mutateShareableMemoLink } =
    useShareableMemoLink({
      dealId,
      memoId,
      enabled: open,
    });

  const handleClickGetShareableMemoLink = useCallback(
    () =>
      createShareableMemoLink(dealId, memoId).then((res) => {
        mutateShareableMemoLink().then(() => {
          copyTextToClipboard({ text: res.url });
          toast.successMessage("Link copied to the clipboard");
          onClose();
        });
      }),
    [dealId, memoId, onClose, mutateShareableMemoLink]
  );

  const handleCopyLink = useCallback(() => {
    if (_.isNil(shareableMemoLink)) {
      toast.errorMessage("There is no shareable link available.");
      return;
    }

    copyTextToClipboard({ text: shareableMemoLink.url });
    toast.successMessage("Link copied to the clipboard");
    onClose();
  }, [shareableMemoLink, onClose]);

  const expireLink = useCallback(
    () =>
      expireShareableMemoLink(dealId, memoId).then(() => {
        toast.successMessage("Link expired");
        mutateShareableMemoLink();
        onClose();
      }),
    [dealId, memoId, onClose, mutateShareableMemoLink]
  );

  const handleClickExpireShareableMemoLink = useCallback(() => {
    showConfirmationDialog({
      onConfirm: expireLink,
      message: "Are you sure you want to expire existing links?",
      confirmButtonText: "Yes",
      confirmButtonColor: "primary",
    });
  }, [showConfirmationDialog, expireLink]);

  const hasShareableMemoLink =
    !_.isNil(shareableMemoLink) && !_.isEmpty(shareableMemoLink.url);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      onClose={onClose}
      dialogDataTestid="memo-sharing-dialog"
    >
      <Box sx={{ padding: theme.spacing(2) }}>
        <Stack
          gap="6px"
          sx={{
            backgroundColor: colors.blue10,
            padding: theme.spacing(2),
            borderRadius: theme.spacing(1),
          }}
        >
          <Text variant="h3">Share via URL link</Text>
          <Text variant="text3" color={colors.gray80}>
            Anyone with the link will be able to view this memo (public access,
            view only).
          </Text>
        </Stack>
        <Stack sx={{ padding: theme.spacing(2, 2, 3) }}>
          {hasShareableMemoLink ? (
            <Link
              style={{
                textDecoration: "underline",
                color: colors.blue100,
                fontWeight: 500,
                wordBreak: "break-all",
              }}
              to={shareableMemoLink.url}
              external
              target="_blank"
              linkDataTestid="memo-share-link"
            >
              {shareableMemoLink.url}
            </Link>
          ) : (
            <Text variant="text3" color={colors.gray60}>
              Currently this document is not shared. There is no active share
              link.
            </Text>
          )}
        </Stack>
        <DialogActions sx={{ padding: 0 }}>
          <Button onClick={onClose} variant="text" data-testid="cancel-button">
            Cancel
          </Button>
          {hasShareableMemoLink ? (
            <>
              <Button
                onClick={handleClickExpireShareableMemoLink}
                color="secondary"
                data-testid="expire-link-button"
              >
                Expire this link
              </Button>
              <Button onClick={handleCopyLink} data-testid="copy-link-button">
                Copy link to clipboard
              </Button>
            </>
          ) : (
            <Button
              onClick={handleClickGetShareableMemoLink}
              data-testid="create-link-button"
            >
              Create share link
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ShareableLinkPopup;
